import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Mail, MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const card = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";
const inputCls = "w-full rounded-xl border border-brand-line dark:border-neutral-700 bg-transparent px-4 py-3 focus:ring-2 focus:ring-brand-red focus:outline-none";

export default function Messaging() {
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const load = () => api.get("/admin/messages").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", { channel, subject, body, audience: "all" });
      toast.success(`Sent to ${data.sent} recipient(s)`); setSubject(""); setBody(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="messaging-page">
      <h1 className="font-display text-4xl">Messaging Center</h1>
      <p className="text-brand-ink/60 dark:text-white/50 mt-2">Email announcements, SMS reminders, emergency cancellations.</p>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <form onSubmit={send} className={`${card} p-6 space-y-4`}>
          <div className="flex gap-2">
            <button type="button" onClick={() => setChannel("email")} className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm ${channel === "email" ? "bg-brand-red text-white" : "border border-brand-line dark:border-neutral-700"}`} data-testid="channel-email"><Mail className="w-4 h-4" /> Email</button>
            <button type="button" onClick={() => setChannel("sms")} className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm ${channel === "sms" ? "bg-brand-red text-white" : "border border-brand-line dark:border-neutral-700"}`} data-testid="channel-sms"><MessageSquare className="w-4 h-4" /> SMS</button>
          </div>
          {channel === "email" && <input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="msg-subject" />}
          <textarea placeholder="Your message to all customers…" value={body} onChange={(e) => setBody(e.target.value)} required rows={6} className={inputCls} data-testid="msg-body" />
          {channel === "sms" && <p className="text-xs text-amber-600">SMS requires Twilio keys — add them in Settings to enable.</p>}
          <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors disabled:opacity-60" data-testid="broadcast-btn"><Send className="w-4 h-4" /> Send to all customers</button>
        </form>
        <div className={`${card} p-6`}>
          <h2 className="font-display text-xl mb-4">Recent broadcasts</h2>
          {history.length === 0 ? <p className="text-brand-ink/50 dark:text-white/40" data-testid="messages-empty">No messages sent yet.</p> : (
            <div className="divide-y divide-brand-line dark:divide-neutral-800">
              {history.map((m) => (
                <div key={m.id} className="py-3" data-testid={`message-${m.id}`}>
                  <div className="flex items-center justify-between text-sm"><span className="font-medium uppercase text-xs tracking-wide text-brand-red">{m.channel}</span><span className="text-brand-ink/40 dark:text-white/40">{m.recipients} sent</span></div>
                  {m.subject && <div className="font-medium mt-1">{m.subject}</div>}
                  <div className="text-sm text-brand-ink/60 dark:text-white/50 mt-1 line-clamp-2">{m.body}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
